/**
 * Small, dependency-free helpers for the inference animation: easing, a
 * deterministic hash so "random" flicker is stable while scrubbing, colors,
 * cached glow sprites, and the one stateful effect (sparks).
 */

export type RGB = [number, number, number];
export type Point = { x: number; y: number };

export const clamp = (x: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** 0..1 progress of `t` through the window that starts at `start` and lasts `dur`. */
export const prog = (t: number, start: number, dur: number) =>
  dur <= 0 ? (t >= start ? 1 : 0) : clamp((t - start) / dur);

/** Rises over `attack`, holds, then falls over `release` once `end` passes. */
export const envelope = (t: number, start: number, end: number, attack = 0.15, release = 0.3) => {
  if (t < start || t > end + release) return 0;
  const up = attack > 0 ? clamp((t - start) / attack) : 1;
  const down = t > end ? 1 - clamp((t - end) / release) : 1;
  return Math.min(up, down);
};

export const easeInCubic = (t: number) => t * t * t;
export const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
export const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
export const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);
export const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

/** Deterministic 0..1 hash of up to three numbers. */
export const hash = (a: number, b = 0, c = 0) => {
  const s = Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453123;
  return s - Math.floor(s);
};

/** Smooth -1..1 value noise, stable for a given seed. */
export const noise = (x: number, seed = 0) => {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f);
  return lerp(hash(i, seed), hash(i + 1, seed), u) * 2 - 1;
};

export const hexToRgb = (hex: string): RGB => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
export const rgba = (c: RGB, a = 1) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${clamp(a)})`;
export const mix = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

export const cubic = (p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return { x: a * p0.x + b * p1.x + c * p2.x + d * p3.x, y: a * p0.y + b * p1.y + c * p2.y + d * p3.y };
};

export const quad = (p0: Point, p1: Point, p2: Point, t: number): Point => {
  const u = 1 - t;
  return { x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x, y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y };
};

/** Point along a polyline, `t` in 0..1 by length. */
export const alongPolyline = (pts: Point[], t: number): Point => {
  let total = 0;
  const lens: number[] = [];
  for (let i = 1; i < pts.length; i++) {
    const l = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    lens.push(l);
    total += l;
  }
  let d = clamp(t) * total;
  for (let i = 0; i < lens.length; i++) {
    if (d <= lens[i] || i === lens.length - 1) {
      const f = lens[i] === 0 ? 0 : d / lens[i];
      return { x: lerp(pts[i].x, pts[i + 1].x, f), y: lerp(pts[i].y, pts[i + 1].y, f) };
    }
    d -= lens[i];
  }
  return pts[pts.length - 1];
};

/**
 * Soft radial glow sprites, one per color, drawn with additive blending.
 * Far cheaper than shadowBlur for hundreds of dots a frame.
 */
export class GlowSprites {
  private cache = new Map<string, HTMLCanvasElement>();
  private static SIZE = 64;

  get(c: RGB): HTMLCanvasElement {
    const key = `${c[0] | 0},${c[1] | 0},${c[2] | 0}`;
    let sprite = this.cache.get(key);
    if (!sprite) {
      sprite = document.createElement("canvas");
      sprite.width = sprite.height = GlowSprites.SIZE;
      const g = sprite.getContext("2d");
      const r = GlowSprites.SIZE / 2;
      const grad = g.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, rgba(c, 1));
      grad.addColorStop(0.25, rgba(c, 0.45));
      grad.addColorStop(0.6, rgba(c, 0.12));
      grad.addColorStop(1, rgba(c, 0));
      g.fillStyle = grad;
      g.fillRect(0, 0, GlowSprites.SIZE, GlowSprites.SIZE);
      this.cache.set(key, sprite);
    }
    return sprite;
  }

  draw(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, c: RGB, alpha = 1) {
    if (alpha <= 0.003 || radius <= 0) return;
    const prevAlpha = ctx.globalAlpha;
    const prevOp = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = prevAlpha * clamp(alpha);
    ctx.drawImage(this.get(c), x - radius, y - radius, radius * 2, radius * 2);
    ctx.globalAlpha = prevAlpha;
    ctx.globalCompositeOperation = prevOp;
  }
}

type Spark = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: RGB;
  gravity: number;
  drag: number;
};

/**
 * The only stateful effect. Everything else in the scene is a pure function of
 * the timeline, so scrubbing just clears the sparks.
 */
export class Sparks {
  private list: Spark[] = [];

  clear() {
    this.list = [];
  }

  burst(
    x: number,
    y: number,
    color: RGB,
    count: number,
    speed: number,
    opts: { gravity?: number; life?: number; size?: number; angle?: number; spread?: number } = {}
  ) {
    const { gravity = 60, life = 0.6, size = 1.6, angle = 0, spread = Math.PI * 2 } = opts;
    for (let i = 0; i < count; i++) {
      const a = angle + (Math.random() - 0.5) * spread;
      const s = speed * (0.35 + Math.random() * 0.65);
      this.list.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: life * (0.6 + Math.random() * 0.4),
        max: life,
        size: size * (0.7 + Math.random() * 0.6),
        color,
        gravity,
        drag: 2.2
      });
    }
    if (this.list.length > 900) this.list.splice(0, this.list.length - 900);
  }

  update(dt: number) {
    if (dt <= 0) return;
    for (const p of this.list) {
      p.vy += p.gravity * dt;
      const d = Math.exp(-p.drag * dt);
      p.vx *= d;
      p.vy *= d;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt;
    }
    this.list = this.list.filter((p) => p.life > 0);
  }

  draw(ctx: CanvasRenderingContext2D, glow: GlowSprites) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.lineCap = "round";
    for (const p of this.list) {
      const a = clamp(p.life / p.max);
      ctx.strokeStyle = rgba(p.color, a);
      ctx.lineWidth = p.size;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x - p.vx * 0.03, p.y - p.vy * 0.03);
      ctx.stroke();
      glow.draw(ctx, p.x, p.y, p.size * 3.5, p.color, a * 0.5);
    }
    ctx.restore();
  }
}
